import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, Clock, Home as HomeIcon, Star, ArrowRight, BadgeCheck, CalendarCheck, ClipboardList, PartyPopper, Quote, MapPin } from "lucide-react";
import api from "../lib/api";
import { BRAND, SERVICE_CATEGORIES, PRICING, TESTIMONIALS, LAGOS_LGAS } from "../data/site";

const WHY = [
  { icon: ShieldCheck, title: "Trusted Professionals", text: "Trained, vetted and uniformed cleaning teams you can welcome into your space with total confidence." },
  { icon: Sparkles, title: "Safe & Effective Products", text: "Family- and pet-safe cleaning products that deliver a genuinely deep, hygienic clean." },
  { icon: Clock, title: "Flexible Scheduling", text: "Mornings, evenings, weekends — we work around your time, not the other way round." },
  { icon: HomeIcon, title: "Homes & Businesses", text: "Apartments, duplexes, offices, shops, salons, restaurants and event halls — we cover it all." },
];

const STEPS = [
  { icon: ClipboardList, title: "Tell us about your space", text: "Pick a service, property size and any add-ons in under 2 minutes." },
  { icon: CalendarCheck, title: "Choose date & location", text: "Select your Lagos area, LGA, and a time slot that suits you." },
  { icon: BadgeCheck, title: "We confirm & arrive", text: "Our team confirms your booking and arrives fully equipped." },
  { icon: PartyPopper, title: "Enjoy your spotless space", text: "Walk into a properly cleaned home or office. Simple as that." },
];

export default function Home() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="hero-gradient noise-overlay relative overflow-hidden pt-28 lg:pt-36 pb-16 lg:pb-24" data-testid="hero-section">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-brand-lime/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-80 h-80 bg-brand-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 animate-fade-up">
                <Sparkles className="w-4 h-4 text-brand-lime" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-white/90">{BRAND.descriptor} — Lagos</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-tight animate-fade-up-1">
                Clean Spaces.<br />
                <span className="text-brand-lime">Better Living.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-xl animate-fade-up-2">
                Lagos' premier residential &amp; commercial property care. Trusted deep cleaning, move-in/move-out cleans,
                event cleanup and monthly plans — delivered with absolute precision. {BRAND.motto}
              </p>
              <div className="mt-8 flex flex-wrap gap-4 animate-fade-up-3">
                <Link to="/booking" data-testid="hero-book-btn" className="px-7 py-3.5 rounded-full bg-brand-lime text-brand-emerald-dark font-bold hover:bg-white hover:shadow-xl hover:shadow-brand-lime/20 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2">
                  Book a Cleaning <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" data-testid="hero-pricing-btn" className="px-7 py-3.5 rounded-full border-2 border-white/30 text-white font-bold hover:bg-white/10 hover:border-brand-gold transition-all duration-300">
                  View Price List
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 animate-fade-up-3">
                {["100% Satisfaction Focus", "Vetted & Trained Staff", "Island & Mainland Coverage"].map((b) => (
                  <span key={b} className="flex items-center gap-2 text-sm text-white/70">
                    <BadgeCheck className="w-4 h-4 text-brand-lime" /> {b}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative animate-fade-up-2">
              <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-black/40 border-4 border-white/10">
                <img src="/assets/hero.jpg" alt="Spotless professionally cleaned living room in Lagos" className="w-full h-[320px] sm:h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3 animate-float-slow">
                <div className="w-11 h-11 rounded-full bg-brand-mint flex items-center justify-center">
                  <Star className="w-5 h-5 text-brand-gold fill-brand-gold" />
                </div>
                <div>
                  <p className="font-display font-extrabold text-brand-emerald text-lg leading-none">500+</p>
                  <p className="text-xs text-brand-muted font-semibold">Spaces cleaned in Lagos</p>
                </div>
              </div>
              <div className="absolute -top-5 -right-3 sm:-right-6 bg-brand-emerald rounded-2xl shadow-xl px-5 py-4 border border-white/10 animate-float-slow" style={{ animationDelay: "1.5s" }}>
                <p className="font-display font-extrabold text-brand-lime text-lg leading-none">From ₦15,000</p>
                <p className="text-xs text-white/70 font-semibold">Residential cleans</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-b border-[#D6E4DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
          {["Ikoyi", "Victoria Island", "Lekki", "Ajah", "Ikeja", "Yaba", "Surulere", "Gbagada", "Magodo", "Festac"].map((a) => (
            <span key={a} className="flex items-center gap-1.5 text-sm font-semibold text-brand-muted">
              <MapPin className="w-3.5 h-3.5 text-brand-lime" /> {a}
            </span>
          ))}
          <span className="text-sm font-bold text-brand-gold">+ all {LAGOS_LGAS.length} Lagos LGAs</span>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28 bg-brand-canvas" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">What We Do</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-emerald tracking-tight">Every space. Properly cleaned.</h2>
            <p className="mt-4 text-brand-muted text-base sm:text-lg">From studio apartments in Surulere to office towers in VI — real services, real prices, zero surprises.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {SERVICE_CATEGORIES.map((s, i) => (
              <Link
                to={`/services#${s.slug}`}
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className="group bg-white rounded-3xl overflow-hidden border border-[#D6E4DC] hover:border-brand-lime/50 shadow-sm hover:shadow-2xl hover:shadow-brand-emerald/10 hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="h-48 overflow-hidden relative">
                  <img src={s.image} alt={s.name} loading={i > 2 ? "lazy" : "eager"} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-emerald-dark/60 to-transparent" />
                  <p className="absolute bottom-3 left-4 text-white/90 text-xs font-bold italic">{s.tagline}</p>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-brand-emerald group-hover:text-brand-lime transition-colors">{s.name}</h3>
                  <p className="mt-2 text-sm text-brand-muted leading-relaxed line-clamp-2">{s.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-24 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">How It Works</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-emerald tracking-tight">Spotless in four simple steps</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative bg-brand-mint rounded-3xl p-7 hover:bg-brand-emerald group transition-colors duration-300" data-testid={`step-card-${i}`}>
                <span className="absolute top-5 right-6 font-display text-5xl font-extrabold text-brand-emerald/10 group-hover:text-white/10 transition-colors">{i + 1}</span>
                <div className="w-12 h-12 rounded-2xl bg-white group-hover:bg-brand-lime/20 flex items-center justify-center mb-5 shadow-sm transition-colors">
                  <s.icon className="w-6 h-6 text-brand-lime" />
                </div>
                <h3 className="font-display font-bold text-lg text-brand-emerald group-hover:text-white transition-colors">{s.title}</h3>
                <p className="mt-2 text-sm text-brand-muted group-hover:text-white/70 transition-colors leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-20 lg:py-28 hero-gradient noise-overlay relative" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Transparent Pricing</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Real prices. No hidden fees.</h2>
              <p className="mt-4 text-white/70 max-w-xl">All prices are starting prices in Naira. Final quotes depend on size, condition and scope of work.</p>
            </div>
            <Link to="/pricing" data-testid="pricing-preview-link" className="px-6 py-3 rounded-full bg-brand-gold text-white font-bold hover:bg-white hover:text-brand-emerald transition-all duration-300 w-fit flex items-center gap-2">
              Full Price List <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="home-pricing-table">
                <thead>
                  <tr className="bg-brand-emerald text-white">
                    <th className="text-left px-6 py-4 font-display font-bold" colSpan={4}>Residential Cleaning <span className="block text-xs font-normal text-white/60 italic">A cleaner home. A healthier you.</span></th>
                  </tr>
                  <tr className="bg-brand-mint text-brand-emerald">
                    {PRICING.residential.columns.map((c) => <th key={c} className="text-left px-6 py-3 font-bold">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {PRICING.residential.rows.map((r, i) => (
                    <tr key={r[0]} className={i % 2 ? "bg-brand-canvas" : "bg-white"}>
                      <td className="px-6 py-3.5 font-semibold text-brand-ink">{r[0]}</td>
                      <td className="px-6 py-3.5 text-brand-muted">{r[1]}</td>
                      <td className="px-6 py-3.5 text-brand-muted">{r[2]}</td>
                      <td className="px-6 py-3.5 font-bold text-brand-emerald">{r[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-28 bg-brand-canvas" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img src="/assets/spray.jpg" alt="Professional cleaner with spray bottle" className="rounded-3xl h-64 w-full object-cover shadow-lg mt-8" />
                <img src="/assets/wiping.jpg" alt="Gloved hand wiping a surface clean" className="rounded-3xl h-64 w-full object-cover shadow-lg" />
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-brand-emerald text-white rounded-2xl px-6 py-4 shadow-xl whitespace-nowrap">
                <p className="font-display font-extrabold text-brand-lime text-xl leading-none">{BRAND.promise}</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Why Neat &amp; Proper</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-emerald tracking-tight mb-8">The proper way to clean Lagos</h2>
              <div className="space-y-6">
                {WHY.map((w) => (
                  <div key={w.title} className="flex gap-4" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="w-12 h-12 rounded-2xl bg-brand-emerald flex items-center justify-center shrink-0 shadow-md">
                      <w.icon className="w-5 h-5 text-brand-lime" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-brand-ink">{w.title}</h3>
                      <p className="text-sm text-brand-muted mt-1 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Lagos Loves Us</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-emerald tracking-tight">Properly cleaned. Properly praised.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure key={t.name} className="bg-brand-mint rounded-3xl p-7 relative hover:-translate-y-1 transition-transform duration-300" data-testid={`testimonial-${i}`}>
                <Quote className="w-8 h-8 text-brand-gold/40 absolute top-6 right-6" />
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 text-brand-gold fill-brand-gold" />)}</div>
                <blockquote className="text-sm text-brand-ink leading-relaxed">"{t.text}"</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-brand-emerald/10">
                  <p className="font-display font-bold text-brand-emerald text-sm">{t.name}</p>
                  <p className="text-xs text-brand-muted">{t.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      {posts.length > 0 && (
        <section className="py-20 lg:py-24 bg-brand-canvas" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">From The Blog</p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-emerald tracking-tight">Tips for a cleaner Lagos life</h2>
              </div>
              <Link to="/blog" data-testid="blog-preview-link" className="font-bold text-brand-emerald hover:text-brand-lime flex items-center gap-2 transition-colors w-fit">All articles <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-preview-card-${p.slug}`} className="group bg-white rounded-3xl overflow-hidden border border-[#D6E4DC] hover:shadow-xl hover:shadow-brand-emerald/10 hover:-translate-y-1 transition-all duration-300">
                  <div className="h-44 overflow-hidden">
                    <img src={p.cover_image || "/assets/hero.jpg"} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-bold text-brand-lime uppercase tracking-wider">{p.category}</span>
                    <h3 className="font-display font-bold text-brand-emerald mt-2 leading-snug group-hover:text-brand-lime transition-colors line-clamp-2">{p.title}</h3>
                    <p className="text-xs text-brand-muted mt-3">{p.reading_time} min read</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-white" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hero-gradient noise-overlay relative rounded-[2.5rem] px-8 py-14 sm:px-16 text-center overflow-hidden shadow-2xl shadow-brand-emerald/30">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-lime/20 rounded-full blur-3xl" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight relative z-10">Ready for a <span className="text-brand-lime">proper</span> clean?</h2>
            <p className="mt-4 text-white/75 max-w-xl mx-auto relative z-10">Book online in 2 minutes, get an instant estimate, and our team will confirm your slot. {BRAND.promise}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 relative z-10">
              <Link to="/booking" data-testid="cta-book-btn" className="px-8 py-4 rounded-full bg-brand-lime text-brand-emerald-dark font-bold hover:bg-white transition-all duration-300 hover:-translate-y-0.5">Book Your Cleaning</Link>
              <a href={BRAND.phoneHref} data-testid="cta-call-btn" className="px-8 py-4 rounded-full border-2 border-white/30 text-white font-bold hover:bg-white/10 transition-all duration-300">Call {BRAND.phone}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
